import { motion } from "motion/react";
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/ui/card";
import { Button } from "@/src/components/ui/button";
import { Separator } from "@/src/components/ui/separator";
import { CheckCircle2, Clock, MapPin, Wallet, Info, Quote as QuoteIcon } from "lucide-react";

export function Workshops() {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Strengthsfinder Workshops Pretoria | Iwantcoaching</title>
        <meta name="description" content="Discover our Strengthsfinder workshops in Pretoria. Specifically customized for individuals who have done the Gallup Strengthsfinder assessment." />
        <meta name="geo.region" content="ZA-GP" />
        <meta name="geo.placename" content="Pretoria" />
        <link rel="canonical" href="https://iwantcoaching.co.za/workshops" />
      </Helmet>
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl font-serif mb-6">Workshops</h1>
            <p className="text-xl text-muted-foreground leading-relaxed italic">
              "Efforts and courage are not enough without purpose and direction"
            </p>
            <p className="text-sm font-semibold uppercase tracking-widest mt-2 text-primary">— John F. Kennedy</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest">
                  Featured Workshop
                </div>
                <h2 className="text-3xl md:text-4xl font-serif leading-tight text-primary">
                  OKAY, I'VE REVIEWED MY TOP FIVE THEMES. <br className="hidden md:block" />
                  <span className="italic">NOW WHAT AM I SUPPOSED TO DO? WHATS NEXT?</span>
                </h2>
                
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    The topic of the workshop says it all! This 5-hour workshop is specifically customized for individuals who have done the Gallup Strengthsfinder assessment and don’t know what the next step is.
                  </p>
                  <p>
                    Strengthsfinder is designed to be a practical tool for individuals, however without the coaching component; it merely becomes a fun, pop quiz with little to zero impact. The process of understanding your strengths involves spending time with an accredited coach who will help you to discover the profound impact of your Top 5 talents.
                  </p>
                  <p>
                    This workshop will be the stepping stone to individual coaching; it’s for those individuals who are not yet sure if they want the coaching but they would like to have something in between the coaching and the assessment!
                  </p>
                </div>
              </motion.div>

              <Separator className="bg-primary/10" />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <h3 className="text-2xl font-serif italic">What to expect</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  The workshop will give you a broad idea of what you can do with your particular strengths and how you can use them in different parts and roles of your life. This will be an interactive, developmental workshop that is sure to inspire you, build your confidence and impact your life.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="rounded-[2rem] border-none bg-secondary/20 shadow-none">
                    <CardHeader>
                      <CardTitle className="text-lg font-serif">Pre-requisites</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      Prior to attending, each attendee is asked to take the Strengthsfinder assessment and submit their results to the facilitator.
                    </CardContent>
                  </Card>
                  <Card className="rounded-[2rem] border-none bg-primary/5 shadow-none">
                    <CardHeader>
                      <CardTitle className="text-lg font-serif">What you'll receive</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      <ul className="space-y-2">
                        <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> A guide for strengths-based discussions.</li>
                        <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Laminated card of your Top 5 talents.</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <h3 className="text-2xl font-serif italic">Topics covered</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                  {[
                    "25 min Strengths DVD from Marcus Buckingham",
                    "Strengths Building versus Weakness Fixing",
                    "Understanding the nature of talent",
                    "5 Clues to Talent",
                    "Getting the most out of your report",
                    "4 Talent clusters",
                    "The Core of understanding Strengths",
                    "Identifying specific roles of Talent Themes",
                    "Different paths to the same destination",
                    "Performance management and strengths",
                    "What about my Weaknesses?",
                    "Your talents every day",
                    "Relationships and talents",
                    "Team contribution using strengths"
                  ].map((topic, i) => (
                    <div key={i} className="flex items-center gap-3 text-muted-foreground">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/40" />
                      <span className="text-sm">{topic}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Sidebar / Info Card */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="sticky top-32"
              >
                <Card className="rounded-[3rem] border-none shadow-xl bg-primary text-white overflow-hidden">
                  <CardHeader className="pb-4">
                    <CardTitle className="font-serif text-2xl italic">Next Workshop</CardTitle>
                    <p className="text-primary-foreground/70 text-sm">Facilitator: Johan Oosthuizen</p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest opacity-60">Time</p>
                          <p className="font-medium">08h30 - 13h30</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest opacity-60">Location</p>
                          <p className="font-medium">Your company in Gauteng</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                          <Wallet className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest opacity-60">Cost</p>
                          <p className="font-medium">R500 per person / R700 per couple</p>
                        </div>
                      </div>
                    </div>

                    <Separator className="bg-white/10" />

                    <div className="space-y-4">
                      <p className="font-serif italic text-lg">What you'll benefit</p>
                      <ul className="space-y-3 text-sm text-primary-foreground/80">
                        <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0" /> Start your strengths journey</li>
                        <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0" /> Personal & professional development</li>
                        <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0" /> Practical implementation ways</li>
                      </ul>
                    </div>

                    <Button className="w-full h-14 rounded-full bg-white text-primary hover:bg-white/90 font-bold text-lg mt-4">
                      Register Now
                    </Button>
                    <p className="text-[10px] text-center opacity-60 mt-4">
                      * Pre-registration is required. Seating is limited.
                    </p>
                  </CardContent>
                </Card>

                <div className="mt-8 p-6 bg-muted rounded-3xl border border-primary/5 flex gap-4">
                  <Info className="w-6 h-6 text-primary shrink-0" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Clients are advised to reconfirm the workshop date and time listed on this website as they are occasionally subject to last-minute change.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <QuoteIcon className="w-12 h-12 text-primary/20 mx-auto mb-6" />
          <p className="text-2xl font-serif italic text-primary leading-relaxed mb-8">
            "The workshop can be done for any organization, company, school or a group with non-related individuals from different walks of life. It gives you that edge on what the next step is and how to put your strengths to work."
          </p>
          <Button variant="outline" className="rounded-full px-8 h-12 border-primary text-primary hover:bg-primary/10">
            Enquire for your Organization
          </Button>
        </div>
      </section>
    </div>
  );
}
